import React, { useState, useEffect } from 'react'
import { Link, useNavigate, useLocation } from 'react-router-dom'
import { toast } from 'react-toastify';
import { getStudentById, getStudentSubjectsById, getStudentWorkSummaryById } from '@app/services/student';
import moment from 'moment';
import { Button } from '@app/components';
import { useSelector } from 'react-redux';
import MoveWorkAheadModal from './MoveWorkAheadModal';
import StudentDetailsCard from './StudentDetailsCard';
import { encodeId } from '@app/helpers/base64url';
import PrintWorkActions from './PrintWorkActions';

const StudentWorkSummary = () => {

    const location = useLocation();
    const id = (location.state as { id?: string })?.id;
    const navigate = useNavigate()
    let obj: any = {}
    const [isLoading, setLoading] = useState(false);
    const [weeklyWork, setWeeklyWork] = useState([]);
    const [studentWork, setStudentWork] = useState([]);
    const [numOfWeeks, setNumOfWeeks] = useState(0);
    const [weeks, setWeeks] = useState([]);
    const [activeWeek, setActiveWeek] = useState(1);
    const [globalWeekCounter, setGlobalWeekCounter] = useState(0);
    const [streamPerWeek, setStreamPerWeek] = useState(0);
    const [student, setStudent] = useState(obj);
    const canAccess = useSelector((state: any) => state.auth.permissions.student);
    const canAccessAutoMark = useSelector((state: any) => state.auth.permissions?.['student-auto-marking']);
    const studentWorkStore = useSelector((state: any) => state.studentWork);
    const [search, setSearch] = useState({subject_id: studentWorkStore?.subject?.id ?? 0});
    const [subjects, setSubjects] = useState([]);
    const [stream1, setStream1] = useState('');
    const [stream2, setStream2] = useState('');
    const [stream3, setStream3] = useState('');
    const [stream4, setStream4] = useState('');
    const [showMoveWorkModal, setShowMoveWorkModal] = useState(false);

    useEffect(() => {
        if (!id) {
            toast.error('Student ID not found');
            navigate(-1);
            return;
        }
        getStudentDetailsById()
        getStudentAllActiveSubjects(id)
        getStudentWorkSummary(id, search.subject_id)
    },[]);

    const getStudentDetailsById = async () => {
        try {
            setLoading(true)
            const resp = await getStudentById(parseInt(id));
            if (resp.success === true) {
                setLoading(false)
                setStudent(resp?.collections)
            } else {
                setLoading(false)
                toast.error(resp?.errors[0]?.msg)
            }
        } catch (error: any) {
            setLoading(false)
            toast.error(error?.response?.message || 'Internal server error');
        }
    };

    const getStudentAllActiveSubjects = async (id) => {
        try {
            setLoading(true)
            const resp = await getStudentSubjectsById(id)
            if (resp.success === true) {
                setLoading(false)
				setSubjects([{id: 0, name: 'Mix and Match'}].concat(resp?.collections))
            } else {
				setLoading(false)
                toast.error(resp?.errors[0]?.msg);
            }
        } catch (error: any) {
			setLoading(false)
            toast.error(error?.message || 'Internal server error');
        }
    };

    const getStudentWorkSummary = async (id, subject_id) => {
        try {
            setLoading(true)
            const resp = await getStudentWorkSummaryById({student_id: id, subject_id: subject_id});
            if (resp.success === true) {
                setLoading(false)
                const summaryData = resp?.collections?.summary;
                setStudentWork(summaryData)
                setStreamPerWeek(resp?.collections?.modules_per_week)
                setGlobalWeekCounter(resp?.collections?.global_week_counter ?? 0)
                setNumOfWeeks(summaryData?.length)
                if(summaryData?.length > 0){
                    // Set weekly work based on current active week or default to first week
                    const weekIndex = Math.min(activeWeek - 1, summaryData.length - 1);
                    setWeeklyWork(summaryData[weekIndex >= 0 ? weekIndex : 0])
                    if(resp?.collections?.modules_per_week >= 1)
                        setStream1(resp?.collections?.module1 ?? '')
                    if(resp?.collections?.modules_per_week >= 2)
                        setStream2(resp?.collections?.module2 ?? '')
                    if(resp?.collections?.modules_per_week >= 3)
                        setStream3(resp?.collections?.module3 ?? '')
                    if(resp?.collections?.modules_per_week >= 4 )
                        setStream4(resp?.collections?.module4 ?? '')
                }else{
                    setWeeklyWork([])
                }
                let weeksArr = [];
                const baseCounter = resp?.collections?.global_week_counter ?? 0;
                for (let i = 0; i < summaryData?.length; i++) {
                    const startDate = summaryData[i]?.[0]?.date ? moment(summaryData[i][0].date).format('DD/MM/YYYY') : '';
                    weeksArr.push({id: i, name: `Week ${baseCounter + i + 1}${startDate ? ' - ' + startDate : ''}`})
                }
                setWeeks(weeksArr);
                return summaryData;
            } else {
                setLoading(false)
                toast.error(resp?.errors[0]?.msg)
                return null;
            }
        } catch (error: any) {
            setLoading(false)
            toast.error(error?.message || 'Internal server error');
            return null;
        }
    };

    const getPaginationContent = () => {
        let content = [];
        const maxVisibleWeeks = 5; // Show 5 weeks at a time
        
        // Calculate start and end week for pagination
        let startWeek = Math.max(1, activeWeek - Math.floor(maxVisibleWeeks / 2));
        let endWeek = Math.min(numOfWeeks, startWeek + maxVisibleWeeks - 1);
        
        // Adjust start if we're near the end
        if (endWeek - startWeek < maxVisibleWeeks - 1) {
            startWeek = Math.max(1, endWeek - maxVisibleWeeks + 1);
        }
        
        // Previous button
        if (activeWeek > 1) {
            content.push(
                <li className="page-item" key="prev">
                    <a className="page-link" href="#" onClick={(e) => changeWeek(e, activeWeek - 1)}>
                        «
                    </a>
                </li>
            );
        }
        
        // First week if not visible
        if (startWeek > 1) {
            content.push(
                <li className="page-item" key={1}>
                    <a className="page-link" href="#" onClick={(e) => changeWeek(e, 1)}>
                        Week {globalWeekCounter + 1}
                    </a>
                </li>
            );
            if (startWeek > 2) {
                content.push(
                    <li className="page-item disabled" key="dots1">
                        <span className="page-link">...</span>
                    </li>
                );
            }
        }
        
        // Visible weeks
        for (let i = startWeek; i <= endWeek; i++) {
            content.push(
                <li className="page-item" key={i}>
                    <a className="page-link" 
                       style={{backgroundColor: activeWeek == i ? '#dee2e6' : '#fff'}} 
                       href="#" 
                       onClick={(e) => changeWeek(e, i)}>
                        Week {globalWeekCounter + i}
                    </a>
                </li>
            );
        }
        
        // Last week if not visible
        if (endWeek < numOfWeeks) {
            if (endWeek < numOfWeeks - 1) {
                content.push(
                    <li className="page-item disabled" key="dots2">
                        <span className="page-link">...</span>
                    </li>
                );
            }
            content.push(
                <li className="page-item" key={numOfWeeks}>
                    <a className="page-link" href="#" onClick={(e) => changeWeek(e, numOfWeeks)}>
                        Week {globalWeekCounter + numOfWeeks}
                    </a>
                </li>
            );
        }
        
        // Next button
        if (activeWeek < numOfWeeks) {
            content.push(
                <li className="page-item" key="next">
                    <a className="page-link" href="#" onClick={(e) => changeWeek(e, activeWeek + 1)}>
                        »
                    </a>
                </li>
            );
        }
        
        return content;
    };

    const changeWeek = (e, week) => {
        e.preventDefault();
        setActiveWeek(week);
        setWeeklyWork(studentWork[week - 1]);
    }

    const handleSubjectChange = async (subject_id) => {
        setSearch({ ...search, subject_id: subject_id })
        getStudentWorkSummary(id, subject_id)
    };

    const handleMoveWorkAhead = () => {
        setShowMoveWorkModal(true);
    }

    const handleCloseMoveWorkModal = () => {
        setShowMoveWorkModal(false);
    }

    const handleMoveWorkSuccess = async () => {
        // Refresh the data and maintain the current week view
        await getStudentWorkSummary(id, search.subject_id);
    }

    // Build normalized weeks for the shared print actions component
    const printWeeks = weeks.map((w: any) => ({
        id: w.id,
        name: w.name,
        week_number: globalWeekCounter + w.id + 1,
        days: studentWork[w.id] || []
    }));

    return (
    <div>
        <section className="content">
            <div className="container-fluid">
                <div className="row">
                    <div className="col-md-12 text-center">
                        <span style={{fontSize: '1.5rem', fontWeight: 'bold'}}>Current Student Work</span>
                    </div>
                    <div className="col-md-12">
                        <StudentDetailsCard student={student} />
                        <div className="card">
                            <div className="card-header">
                                <h3 className="card-title">Work Summary {studentWork.length == 0 ? '' : `- Week ${globalWeekCounter + activeWeek}`}</h3>
                                <div className="card-tools">
                                    { canAccess?.['move-work-ahead'] && (
                                        <Button type="button" onClick={handleMoveWorkAhead} className="btn btn-warning btn-sm" disabled={studentWork.length==0}><i className="fas fa-arrow-right"></i> Move Work Ahead</Button>
                                    )}
                                </div>
                            </div>
                            <div className="card-body">
                                { (canAccess?.['print-worksheets'] || canAccess?.['print-handbooks'] || canAccess?.['print-weeks-summary']) && (
                                    <PrintWorkActions
                                        key={`print-${search.subject_id}`}
                                        studId={id}
                                        student={student}
                                        subjectId={search.subject_id}
                                        weeks={printWeeks}
                                        canAccess={canAccess}
                                        isLoading={isLoading}
                                    />
                                )}
                                <div className="row mb-3">
                                    <div className="col-sm-4">
                                        <label>You are viewing work for the subject</label>
                                        <select
                                            className="form-control"
                                            name="subject_id"
                                            onChange={(e) => handleSubjectChange(e.target.value)}
                                            value={search.subject_id}
                                        >
                                            {subjects.length > 0 && subjects.map((obj) => (
                                                <option key={obj.id} value={obj.id}>
                                                    {obj.name}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                </div>
                                <table className="table table-bordered">
                                    { weeklyWork.length > 0 && (
                                    <thead>
                                        <tr>
                                            <th style={{ width: 10 }}>#</th>
                                            <th>Date</th>
                                            { streamPerWeek >= 1 && (
                                                <th>{ stream1 }</th>
                                            )}
                                            { streamPerWeek >= 2 && (
                                                <th>{ stream2 }</th>
                                            )}
                                            { streamPerWeek >= 3 && (
                                                <th>{ stream3 }</th>
                                            )}
                                            { streamPerWeek >= 4 && (
                                                <th>{ stream4 }</th>
                                            )}
                                        </tr>
                                    </thead>
                                    )}
                                    <tbody>
                                        {weeklyWork.length > 0 && weeklyWork.map((obj, index) => (
                                            <tr key={index}>
                                                <td>{index + 1}.</td>
                                                <td>{ moment(obj.date).format('DD/MM/YYYY, dddd') }</td>
                                                { streamPerWeek >= 1 && (
                                                    <td>{ obj?.modules !== null ? ( Array.prototype.map.call(obj?.modules.filter(mod => mod.module == 'module1'), function(module) { return module?.name + '-' + module?.worksheet_number }) ).join(', ') : '---'}</td>
                                                )}
                                                { streamPerWeek >= 2 && (
                                                    <td>{ obj?.modules !== null ? ( Array.prototype.map.call(obj?.modules.filter(mod => mod.module == 'module2'), function(module) { return module?.name + '-' + module?.worksheet_number }) ).join(', ') : '---'}</td>
                                                )}
                                                { streamPerWeek >= 3 && (
                                                    <td>{ obj?.modules !== null ? ( Array.prototype.map.call(obj?.modules.filter(mod => mod.module == 'module3'), function(module) { return module?.name + '-' + module?.worksheet_number }) ).join(', ') : '---'}</td>
                                                )}
                                                { streamPerWeek >= 4 && (
                                                    <td>{ obj?.modules !== null ? ( Array.prototype.map.call(obj?.modules.filter(mod => mod.module == 'module4'), function(module) { return module?.name + '-' + module?.worksheet_number }) ).join(', ') : '---'}</td>
                                                )}
                                            </tr>
                                        ))}
                                        {weeklyWork.length == 0 && (
                                            <tr>
                                                <td className='text-center' colSpan={3}>There are no work summary to display</td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>

                            </div>
                            <div className="card-footer">
                                <div className="d-flex justify-content-between align-items-center flex-wrap" style={{ gap: 8 }}>
                                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                                        <Button className="btn btn-default btn-sm" onClick={() => navigate(-1)}>
                                            <i className="fas fa-times mr-1" />Close
                                        </Button>
                                        { student?.status == 'active' && canAccess?.['set-work'] && (
                                            <Link className="btn btn-default btn-sm" to={`/student/set-work/${encodeId(id)}`} role="button">
                                                <i className="fas fa-network-wired mr-1" />Set Student Work
                                            </Link>
                                        )}
                                        { canAccessAutoMark?.list && (
                                            <Link className="btn btn-default btn-sm" to={`/student/${encodeId(id)}/auto-marking`} role="button">
                                                <i className="fas fa-robot mr-1" />Auto Marking
                                            </Link>
                                        )}
                                        { canAccess?.['work-history'] && (
                                            <Link className="btn btn-default btn-sm" to="/student/work-summary-history" state={{ id: id }} role="button">
                                                <i className="fas fa-history mr-1" />Work Summary History
                                            </Link>
                                        )}
                                        { canAccess?.['work-history'] && (
                                            <Link className="btn btn-default btn-sm" to="/student/module-assignment-list" state={{ id: id }} role="button">
                                                <i className="fas fa-list mr-1" />Module Assignment List
                                            </Link>
                                        )}
                                    </div>
                                    <ul className="pagination pagination-sm m-0">
                                        { getPaginationContent() }
                                    </ul>
                                </div>
                            </div>
                        </div>
                    </div>
                    
                </div>
            </div>
        </section>
        <MoveWorkAheadModal
            show={showMoveWorkModal}
            handleClose={handleCloseMoveWorkModal}
            student={student}
            subjects={subjects}
            subjectId={search.subject_id}
            weeks={weeks}
            isLoading={isLoading}
            globalWeekCounter={globalWeekCounter}
            studentId={id}
            onSuccess={handleMoveWorkSuccess}
        />
    </div>
  )
}

export default StudentWorkSummary;